import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  fetchDashboardAnalytics,
  fetchAnalytics,
  getAnalyticsData,
  getLinkAnalytics,
  getUserAnalytics,
  getAnalyticsDataByLink,
  creatorUserDetails,
  getCreatorAllSubmissions,
  campaignAnalysis,
  getCampaignKpis,
  getCampaignTopPosts,
  getCampaignCreatorsReach,
  totalSellChart,
  earnedMediaValueChart,
  contentAnalysisChart,
  engagementAnalysisChart,
  creatorAnalysis,
  roasAnalysisChart,
  getBrandSentimentAnalysis,
  getCampaignChart,
  getCampaignCreatorList,
  getCampaignRoasAnalysisChart,
  fetchTopLinks,
  fetchTopUsers,
  fetchTopRegions,
  fetchTopCountries,
  fetchTopDevices,
  fetchTopPages,
  fetchTopReferrers,
  fetchTopBrowsers,
  fetchTopPlatforms,
  fetchTotalClicks,
  fetchNewClicks,
  fetchTotalUsers,
  fetchNewUsers,
  fetchTotalLinks,
  fetchNewLinks,
  fetchTotalAnnualSubscriptions,
  fetchTotalMonthlySubscriptions,
  fetchUserStatistics,
  fetchPerformanceGraphs,
  fetchUsersEvolution,
  fetchLinksEvolution,
  fetchUserCreationOverTime,
  fetchLinkPerformanceOverTime,
} from '../../services/analyticsService';
import { TimeInterval } from '../../types/types';

const STALE_TIME = 5 * 60 * 1000;

export interface ValueVsSpendMetrics {
  roi: number;
  averageRoas: number;
  totalCampaignValue: string;
  cgc: string;
  totalEmv: string;
  totalSales: string;
  totalSpent: number;
  approvedCashbackCount: number;
}

export interface PerformanceMetricsDeltas {
  totalPosts: number | null;
  totalViews: number | null;
  totalLikes: number | null;
  totalShares: number | null;
  engagementRate: number | null;
}

export interface PerformanceMetrics {
  totalPosts: number;
  totalImpressions: number;
  totalLikes: number;
  totalShares: number;
  engagementRate: number;
  deltas?: PerformanceMetricsDeltas;
}

/**
 * Raw payload of GET /analytics/dashboard/:campaignId/campaignChart.
 * Monetary fields (totalSpent, totalEmv, totalSales, totalCampaignValue, cgc,
 * totalEstimatedSpending) ship as decimal strings — consumers must Number() them.
 */
export interface CampaignChartResponse {
  labels: string[];
  // Nullable: backend uses `null` for future-pad buckets (chart should not
  // plot) vs `0` for real "no activity this period" (flat bar/line).
  spendingData: (number | null)[];
  emvData: (number | null)[];
  salesData: (number | null)[];
  totalEstimatedSpending: string;
  totalEmv: string;
  totalSales: string;
  totalSpent: string;
  cgc: string;
  totalCampaignValue: string;
  roi: number;
  averageRoas: number;
  approvedCashbackCount: number;
  totalPosts: number;
  totalImpressions: number;
  totalLikes: number;
  totalShares: number;
  totalComments: number;
  engagementRate: number;
  /** Index of the "now"/current period bucket in `labels`. Frontend uses
   *  this for the highlight marker — derived from server time, not from
   *  local Date.toLocaleString() (which breaks across locales). Null when
   *  `now` falls outside the rendered window (e.g. past-only custom range). */
  currentLabelIndex: number | null;
  deltas: PerformanceMetricsDeltas;
  lastRefreshedAt: string;
}

/**
 * brandMetrics block of GET /analytics/dashboard/:brandId.
 * Most monetary fields here are numbers; only cgc and totalBrandValue are decimal strings.
 */
export interface BrandMetricsResponse {
  totalSpent: number;
  totalParticipants: number;
  totalImpressions: number;
  totalPosts: number;
  totalLikes: number;
  totalSales: number;
  totalEmv: number;
  totalShares: number;
  totalComments: number;
  engagementRate: number;
  cgc: string;
  totalBrandValue: string;
  roi: number;
  averageRoas: number;
  approvedCashbackCount: number;
}

export interface CampaignAnalysisResponse {
  campaignMetrics: {
    totalCampaigns: number;
    activeCampaigns: number;
    completedCampaigns: number;
    pausedCampaigns: number;
    scheduledCampaigns: number;
  };
  brandMetrics: BrandMetricsResponse;
  lastRefreshedAt: string;
}

export const useDashboardAnalyticsQuery = (
  timeInterval: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.dashboard(timeInterval),
    queryFn: () => fetchDashboardAnalytics(timeInterval),
    staleTime: STALE_TIME,
    enabled,
  });

export const useFullAnalyticsQuery = (timeInterval: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.full(timeInterval),
    queryFn: () => fetchAnalytics(timeInterval),
    staleTime: STALE_TIME,
    enabled,
  });

export const useGeneralAnalyticsQuery = (
  timeInterval: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.general(timeInterval),
    queryFn: () => getAnalyticsData(timeInterval),
    staleTime: STALE_TIME,
    enabled,
  });

export const useLinkAnalyticsQuery = (
  linkId: string,
  interval: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.byLink(linkId, interval),
    queryFn: () => getLinkAnalytics(linkId, interval as TimeInterval),
    staleTime: STALE_TIME,
    enabled: enabled && !!linkId,
  });

export const useUserAnalyticsQuery = (
  userId: string,
  interval: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.byUser(userId, interval),
    queryFn: () => getUserAnalytics(userId, interval as TimeInterval),
    staleTime: STALE_TIME,
    enabled: enabled && !!userId,
  });

export const useUserLinkAnalyticsQuery = (
  userId: string,
  linkId: string,
  interval: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.byUserLink(userId, linkId, interval),
    queryFn: () => getAnalyticsDataByLink(interval, linkId, userId),
    staleTime: STALE_TIME,
    enabled: enabled && !!userId && !!linkId,
  });

export const useCreatorAllSubmissionsQuery = <T = unknown>(
  brandId: string,
  userId: string,
  enabled = true,
) =>
  useQuery<T>({
    queryKey: queryKeys.analytics.creatorAllSubmissions(brandId, userId),
    queryFn: () => getCreatorAllSubmissions(brandId, userId),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId && !!userId,
  });

export const useCreatorUserDetailsQuery = (
  brandId: string,
  userId: string,
  campaignId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.creatorUserDetails(
      brandId,
      userId,
      campaignId,
    ),
    queryFn: () => creatorUserDetails(brandId, userId, campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId && !!userId && !!campaignId,
  });

export const useCampaignAnalysisQuery = (
  brandId: string,
  enabled = true,
  filter = 'all-time',
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: queryKeys.analytics.campaignAnalysis(
      brandId,
      filter,
      startDate,
      endDate,
    ),
    queryFn: () => campaignAnalysis(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId,
  });

export const useTotalSellChartQuery = (
  brandId: string,
  filter: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.totalSellChart(brandId, filter),
    queryFn: () => totalSellChart(brandId, filter),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId && !!filter,
  });

export const useEarnedMediaValueChartQuery = (
  brandId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.earnedMediaValueChart(brandId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => earnedMediaValueChart(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!brandId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useContentAnalysisChartQuery = (
  brandId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.contentAnalysis(brandId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => contentAnalysisChart(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!brandId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useEngagementAnalysisChartQuery = (
  brandId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.engagementAnalysis(brandId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => engagementAnalysisChart(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!brandId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useCreatorAnalysisQuery = (
  brandId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.creatorAnalysis(brandId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => creatorAnalysis(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!brandId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useRoasAnalysisChartQuery = (
  brandId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.roasAnalysis(brandId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => roasAnalysisChart(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!brandId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useBrandSentimentQuery = (
  brandId: string,
  enabled = true,
  filter?: string,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: queryKeys.analytics.brandSentiment(
      brandId,
      filter,
      startDate,
      endDate,
    ),
    queryFn: () =>
      getBrandSentimentAnalysis(brandId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId,
  });

export const useCampaignChartQuery = (
  campaignId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.campaignChart(campaignId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () => getCampaignChart(campaignId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!campaignId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

/** Campaign-scoped KPIs for the dashboard's live-campaign panel. */
export const useCampaignKpisQuery = (campaignId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.campaignKpis(campaignId),
    queryFn: () => getCampaignKpis(campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!campaignId,
  });

/** The campaign's top six posts for the dashboard panel. */
export const useCampaignTopPostsQuery = (campaignId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.campaignTopPosts(campaignId),
    queryFn: () => getCampaignTopPosts(campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!campaignId,
  });

/** Pooled Instagram audience for the dashboard's creators-reach panel. */
export const useCampaignCreatorsReachQuery = (
  campaignId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.campaignCreatorsReach(campaignId),
    queryFn: () => getCampaignCreatorsReach(campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!campaignId,
  });

export const useCampaignCreatorListQuery = (
  campaignId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.campaignCreatorList(campaignId),
    queryFn: () => getCampaignCreatorList(campaignId),
    staleTime: STALE_TIME,
    enabled: enabled && !!campaignId,
  });

export const useCampaignRoasChartQuery = (
  campaignId: string,
  filter: string,
  enabled = true,
  startDate?: string,
  endDate?: string,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.analytics.campaignRoasChart(campaignId, filter),
      startDate ?? null,
      endDate ?? null,
    ],
    queryFn: () =>
      getCampaignRoasAnalysisChart(campaignId, filter, startDate, endDate),
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      !!campaignId &&
      (filter === 'custom' ? !!startDate && !!endDate : !!filter),
  });

export const useTopLinksQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topLinks(filter),
    queryFn: () => fetchTopLinks(filter),
    staleTime: STALE_TIME,
  });

export const useTopUsersQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topUsers(filter),
    queryFn: () => fetchTopUsers(filter),
    staleTime: STALE_TIME,
  });

export const useTopRegionsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topRegions(filter),
    queryFn: () => fetchTopRegions(filter),
    staleTime: STALE_TIME,
  });

export const useTopCountriesQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topCountries(filter),
    queryFn: () => fetchTopCountries(filter),
    staleTime: STALE_TIME,
  });

export const useTopDevicesQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topDevices(filter),
    queryFn: () => fetchTopDevices(filter),
    staleTime: STALE_TIME,
  });

export const useTopPagesQuery = () =>
  useQuery({
    queryKey: queryKeys.analytics.topPages(),
    queryFn: fetchTopPages,
    staleTime: STALE_TIME,
  });

export const useTopReferrersQuery = () =>
  useQuery({
    queryKey: queryKeys.analytics.topReferrers(),
    queryFn: fetchTopReferrers,
    staleTime: STALE_TIME,
  });

export const useTopBrowsersQuery = () =>
  useQuery({
    queryKey: queryKeys.analytics.topBrowsers(),
    queryFn: fetchTopBrowsers,
    staleTime: STALE_TIME,
  });

export const useTopPlatformsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.topPlatforms(filter),
    queryFn: () => fetchTopPlatforms(filter),
    staleTime: STALE_TIME,
  });

export const useTotalClicksQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.totalClicks(filter),
    queryFn: () => fetchTotalClicks(filter),
    staleTime: STALE_TIME,
  });

export const useNewClicksQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.newClicks(filter),
    queryFn: () => fetchNewClicks(filter),
    staleTime: STALE_TIME,
  });

export const useTotalUsersQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.totalUsers(filter),
    queryFn: () => fetchTotalUsers(filter),
    staleTime: STALE_TIME,
  });

export const useNewUsersQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.newUsers(filter),
    queryFn: () => fetchNewUsers(filter),
    staleTime: STALE_TIME,
  });

export const useTotalLinksQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.totalLinks(filter),
    queryFn: () => fetchTotalLinks(filter),
    staleTime: STALE_TIME,
  });

export const useNewLinksQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.newLinks(filter),
    queryFn: () => fetchNewLinks(filter),
    staleTime: STALE_TIME,
  });

export const useTotalAnnualSubsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.totalAnnualSubs(filter),
    queryFn: () => fetchTotalAnnualSubscriptions(filter),
    staleTime: STALE_TIME,
  });

export const useTotalMonthlySubsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.totalMonthlySubs(filter),
    queryFn: () => fetchTotalMonthlySubscriptions(filter),
    staleTime: STALE_TIME,
  });

export const useUserStatisticsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.userStatistics(filter),
    queryFn: () => fetchUserStatistics(filter),
    staleTime: STALE_TIME,
  });

export const usePerformanceGraphsQuery = (filter?: TimeInterval) =>
  useQuery({
    queryKey: queryKeys.analytics.performanceGraphs(filter),
    queryFn: () => fetchPerformanceGraphs(filter),
    staleTime: STALE_TIME,
  });

export const useUsersEvolutionQuery = (filter: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.usersEvolution(filter),
    queryFn: () => fetchUsersEvolution(filter),
    staleTime: STALE_TIME,
    enabled: enabled && !!filter,
  });

export const useLinksEvolutionQuery = (filter: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.linksEvolution(filter),
    queryFn: () => fetchLinksEvolution(filter),
    staleTime: STALE_TIME,
    enabled: enabled && !!filter,
  });

export const useUserCreationOverTimeQuery = (filter: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.analytics.userCreationOverTime(filter),
    queryFn: () => fetchUserCreationOverTime(filter),
    staleTime: STALE_TIME,
    enabled: enabled && !!filter,
  });

export const useLinkPerformanceOverTimeQuery = (
  filter: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.analytics.linkPerformanceOverTime(filter),
    queryFn: () => fetchLinkPerformanceOverTime(filter),
    staleTime: STALE_TIME,
    enabled: enabled && !!filter,
  });
